import React, { useMemo } from 'react';

const DashboardTab = ({ data, t }) => {
    const fallback = t('receptionOutpatients.details.fallback.na', { defaultValue: 'N/A' });

    const visitDate = useMemo(() => {
        if (!data?.visit_date) return fallback;
        if (typeof data.visit_date === 'string') return data.visit_date.split(' ')[0];
        try {
            return new Date(data.visit_date).toISOString().split('T')[0];
        } catch {
            return fallback;
        }
    }, [data?.visit_date, fallback]);

    const assignedDoctor = useMemo(() => {
        const v = data?.vet;
        return v?.full_name || `${v?.first_name || ''} ${v?.last_name || ''}`.trim() || fallback;
    }, [data?.vet, fallback]);

    if (!data) return null;

    // ---- Status row ----
    const statusKey = String(data?.status || 'active').toLowerCase();
    const statusClass = ['active', 'checkout', 'completed', 'canceled', 'confirmed'].includes(statusKey) ? statusKey : 'active';
    const statusLabel = t(`receptionOutpatients.tab.status.${statusKey}`, { defaultValue: data?.status || fallback });

    // ---- Diagnosis chips ----
    const diagnosisTags = String(data?.final_diag || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

    // ---- Where things stand (all fields already on `data` from the details endpoint) ----
    const serviceCount = Array.isArray(data?.visit_details) ? data.visit_details.length : 0;
    const billedTotal = typeof data?.total_amount === 'number' ? data.total_amount : parseFloat(data?.total_amount) || 0;
    const paidTotal = typeof data?.downpayment === 'number' ? data.downpayment : parseFloat(data?.downpayment) || 0;
    const balanceTotal = Math.max(0, billedTotal - paidTotal);
    const showStats = serviceCount > 0 || billedTotal > 0;

    const formatAmount = (value) =>
        Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Pet identity now lives in the modal header (OutpatientDetailsOverlay)

    // ---- Guardian one-liner ----
    const guardianName = data?.pet_owner?.full_name ||
        `${data?.pet_owner?.first_name || ''} ${data?.pet_owner?.last_name || ''}`.trim() ||
        fallback;
    const guardianPhone = data?.pet_owner?.phone || fallback;

    return (
        <div className="dashboard-tab-content tab-content-inner dashboard-summary">
            <div className="dashboard-cards-grid">
                {/* ===== Visit: status + serial + date + doctor, one card ===== */}
                <div className="dashboard-group-header">
                    {t('receptionOutpatients.details.sections.visitInformation', { defaultValue: 'Visit' })}
                </div>
                <div className="info-card-premium info-card-compact summary-full-row">
                    <div className="card-icon"><i className="fas fa-calendar-check"></i></div>
                    <div className="card-details">
                        <div className="summary-visit-line">
                            <span className={`status-badge ${statusClass}`}>{statusLabel}</span>
                            <span className="summary-visit-item">
                                <label>{t('receptionOutpatients.details.labels.serialNumber', { defaultValue: 'Serial Number' })}</label>
                                <p>{data?.out_serial_num || fallback}</p>
                            </span>
                            <span className="summary-visit-item">
                                <label>{t('receptionOutpatients.details.labels.visitDate', { defaultValue: 'Visit Date' })}</label>
                                <p>{visitDate}</p>
                            </span>
                            <span className="summary-visit-item">
                                <label>{t('receptionOutpatients.details.labels.assignedDoctor', { defaultValue: 'Assigned Doctor' })}</label>
                                <p>{assignedDoctor}</p>
                            </span>
                        </div>
                    </div>
                </div>

                {/* ===== Why they're here ===== */}
                <div className="dashboard-group-header">
                    {t('receptionOutpatients.details.summarySections.whyHere', { defaultValue: "Why they're here" })}
                </div>
                <div className="info-card-premium info-card-compact summary-full-row">
                    <div className="card-icon"><i className="fas fa-comment-medical"></i></div>
                    <div className="card-details">
                        <div className="summary-field">
                            <span className="summary-field-label">
                                {t('receptionOutpatients.details.summarySections.mainComplaint', { defaultValue: 'Main complaint' })}
                            </span>
                            <span className="summary-field-value">
                                {data?.main_complaint || data?.reason || (
                                    <span className="summary-muted-text">
                                        {t('receptionOutpatients.details.summarySections.noComplaint', { defaultValue: 'No complaint recorded' })}
                                    </span>
                                )}
                            </span>
                        </div>
                        <div className="summary-field">
                            <span className="summary-field-label">
                                {t('receptionOutpatients.details.summarySections.initialImpression', { defaultValue: 'Initial impression' })}
                            </span>
                            <span className="summary-field-value">
                                {data?.initial_diag || (
                                    <span className="summary-muted-text">—</span>
                                )}
                            </span>
                        </div>
                        <div className="summary-field">
                            <span className="summary-field-label">
                                {t('receptionOutpatients.details.summarySections.finalDiagnosis', { defaultValue: 'Final diagnosis' })}
                            </span>
                            <span className="summary-field-value">
                                {diagnosisTags.length > 0 ? (
                                    <span className="diag-tags">
                                        {diagnosisTags.map((tag, idx) => (
                                            <span key={idx} className="diag-tag">{tag}</span>
                                        ))}
                                    </span>
                                ) : (
                                    <span className="summary-muted-text">
                                        {t('receptionOutpatients.details.summarySections.noDiagnosis', { defaultValue: 'No diagnosis recorded yet' })}
                                    </span>
                                )}
                            </span>
                        </div>
                    </div>
                </div>

                {/* ===== Guardian: one card ===== */}
                <div className="dashboard-group-header">
                    {t('receptionOutpatients.details.sections.guardianInformation', { defaultValue: 'Guardian' })}
                </div>
                <div className="info-card-premium info-card-compact summary-full-row">
                    <div className="card-icon"><i className="fas fa-user-tie"></i></div>
                    <div className="card-details">
                        <p className="summary-pet-line">
                            {guardianName} · <i className="fas fa-phone-alt" style={{ fontSize: '0.8rem', color: '#00D4B3' }}></i> {guardianPhone}
                        </p>
                    </div>
                </div>

                {/* ===== Where things stand ===== */}
                {showStats && (
                    <>
                        <div className="dashboard-group-header">
                            {t('receptionOutpatients.details.summarySections.whereThingsStand', { defaultValue: 'Where things stand' })}
                        </div>
                        <div className="summary-full-row summary-stats-row">
                            <div className="summary-stat">
                                <label>{t('receptionOutpatients.details.summarySections.services', { defaultValue: 'Services' })}</label>
                                <p>{serviceCount}</p>
                            </div>
                            <div className="summary-stat">
                                <label>{t('receptionOutpatients.details.summarySections.billed', { defaultValue: 'Billed' })}</label>
                                <p>{formatAmount(billedTotal)}</p>
                            </div>
                            <div className="summary-stat">
                                <label>{t('receptionOutpatients.details.summarySections.paid', { defaultValue: 'Paid' })}</label>
                                <p>{formatAmount(paidTotal)}</p>
                            </div>
                            <div className="summary-stat">
                                <label>{t('receptionOutpatients.details.summarySections.balance', { defaultValue: 'Balance' })}</label>
                                <p className={balanceTotal > 0 ? 'balance' : ''}>{formatAmount(balanceTotal)}</p>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DashboardTab;
